/**
 * Sync wire protocol: the Mutation shape the server accepts, plus base64
 * helpers for stroke point payloads.
 *
 * IMPORTANT: this is a deliberate standalone mirror of the server contract —
 * `Mutation` must match `functions/api/router.ts` exactly, and the b64
 * helpers must byte-match `functions/lib/b64.ts` (same chunked algorithm).
 * App and functions don't share a build, so keep both sides in lockstep when
 * either changes.
 *
 * Framework-free: no Svelte (or any UI) imports.
 */
import type { Rect } from '../ink/stroke';

export type Mutation =
  | {
      kind: 'notebook' | 'page';
      id: string;
      data: unknown;
      updatedAt: number;
      deletedAt?: number | null;
      rev?: number;
    }
  | {
      kind: 'stroke';
      id: string;
      pageId: string;
      meta: unknown;
      pointsB64?: string;
      deletedAt?: number | null;
      createdAt: number;
      /**
       * Timestamp of a deletion-state mutation. Required for tombstone
       * *clears* (deletedAt: null) so the clear can LWW-compare against an
       * existing tombstone; ignored otherwise.
       */
      updatedAt?: number;
    }
  | {
      kind: 'textbox';
      id: string;
      pageId: string;
      /** Full TextBox record (server stores it opaquely; LWW by updatedAt like pages). */
      data: unknown;
      updatedAt: number;
      deletedAt?: number | null;
    }
  | {
      // Textbox-identical shape and LWW treatment (the server declares the two
      // as one variant with kind: 'textbox' | 'image'); separate variant here
      // so Extract<Mutation, { kind: 'textbox' }> keeps working.
      kind: 'image';
      id: string;
      pageId: string;
      /** Full Image record (server stores it opaquely; LWW by updatedAt like pages). */
      data: unknown;
      updatedAt: number;
      deletedAt?: number | null;
    }
  | {
      kind: 'purge';
      target: 'notebook' | 'page';
      id: string;
      /** Client-side purge timestamp; informational only (purge is terminal, no LWW). */
      at?: number;
    };

/** Shape the client packs into a stroke mutation's `meta` (server stores it opaquely). */
export interface StrokeMeta {
  tool: 'pen' | 'highlighter';
  color: string;
  baseWidth: number;
  bbox: Rect;
  createdAt: number;
}

/** Outbox row value (IndexedDB `outbox` store, autoIncrement key). */
export interface OutboxEntry {
  mutation: Mutation;
  enqueuedAt: number;
}

// ---------------------------------------------------------------------------
// Base64 <-> bytes (mirror of functions/lib/b64.ts — keep byte-identical)
// ---------------------------------------------------------------------------

const CHUNK = 0x8000; // avoid call-stack limits in String.fromCharCode(...spread)

export function bytesToB64(bytes: Uint8Array): string {
  let bin = '';
  for (let i = 0; i < bytes.length; i += CHUNK) {
    bin += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(bin);
}

export function b64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}
